import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TimePeriod } from '../../models/time-period.enum';
import { IListNonConformanceModel } from 'src/app/non-conformance-user/models/list-non-conformance.model';
// Trend-related import - temporarily commented out, may be needed in the future
// import { TrendValue, TrendValueEnum } from 'src/app/shared/models/trend-value.model';
import { LanguageService } from 'src/app/core/services/language.service';
import { DeviationsOverTimeDataService } from './deviations-over-time-data.service';
import { DashboardListItem } from '../models/dashboard-list-item.model';
import { DeviationDataPoint } from '../../models/deviation-data-point.model';
import { TypeColorMapping } from '../../models/legend-data.model';
import { LegendData } from '../../models/legend-data.model';
import { ChartScale } from '../../models/chart-scale.model';
import { ChartColors } from '../../models/chart-colors.model';
import { IdName } from 'src/app/shared/models/id-name';

// Trend-related interface - temporarily commented out, may be needed in the future
// export interface TrendCalculationResult {
//   trend: TrendValue;
//   trendText: string;
//   hasInsufficientData: boolean;
// }

@Injectable({
  providedIn: 'root',
})
export class DeviationsOverTimeProcessingService {
  // Constants for trend calculation thresholds - temporarily commented out, may be needed in the future
  // private static readonly TREND_VERY_POSITIVE_THRESHOLD = -20; // Decrease > 20%
  // private static readonly TREND_POSITIVE_THRESHOLD = -5; // Decrease 5-20%
  // private static readonly TREND_NEGATIVE_THRESHOLD = 5; // Increase 5-20%
  // private static readonly TREND_VERY_NEGATIVE_THRESHOLD = 20; // Increase > 20%
  // Constant for AllTime start date
  private static readonly ALL_TIME_START_YEAR = 2000;
  private currentLanguage: string | null = null;

  // Icelandic month names (Chrome doesn't support is-IS locale in toLocaleString)
  private static readonly ICELANDIC_MONTHS_SHORT: string[] = [
    'jan',
    'feb',
    'mar',
    'apr',
    'maí',
    'jún',
    'júl',
    'ágú',
    'sep',
    'okt',
    'nóv',
    'des',
  ];
  private static readonly ICELANDIC_MONTHS_LONG: string[] = [
    'janúar',
    'febrúar',
    'mars',
    'apríl',
    'maí',
    'júní',
    'júlí',
    'ágúst',
    'september',
    'október',
    'nóvember',
    'desember',
  ];

  constructor(
    private languageService: LanguageService,
    private dataService: DeviationsOverTimeDataService,
    private translateService: TranslateService
  ) {
    this.currentLanguage = this.languageService.getCurrentLanguage();
  }

  translateMonthLabels(labels: string[]): string[] {
    const currentLang = this.languageService.getCurrentLanguage();
    const locale = this.languageService.getLocaleForLanguage(currentLang);
    const monthAbbrMap: { [key: string]: number } = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11,
    };

    return labels.map((label) => {
      // Check if label is a month abbreviation (e.g., "Jan", "Feb")
      const monthLower = label.toLowerCase().trim();
      if (monthAbbrMap.hasOwnProperty(monthLower)) {
        // It's a month abbreviation, translate it
        const monthIndex = monthAbbrMap[monthLower];
        const date = new Date(2000, monthIndex, 1);
        const translatedMonth = this.getMonthNameWithFallback(date, 'short', locale);
        return translatedMonth;
      }

      // Check if label is "Month Year" format (e.g., "Nov 2023")
      const monthYearMatch = label.match(/^([A-Za-z]+)\s+(\d{4})$/);
      if (monthYearMatch) {
        const monthShort = monthYearMatch[1].toLowerCase();
        const year = monthYearMatch[2];
        if (monthAbbrMap.hasOwnProperty(monthShort)) {
          const monthIndex = monthAbbrMap[monthShort];
          const date = new Date(parseInt(year), monthIndex, 1);
          const translatedMonth = this.getMonthNameWithFallback(date, 'short', locale);
          return `${translatedMonth} ${year}`;
        }
      }

      // Not a month label, return as is (e.g., day numbers for Last30Days)
      return label;
    });
  }

  calculateChartScale(data: number[]): ChartScale {
    // Calculate dynamic max based on the maximum total value
    const maxTotal = Math.max(...data, 1); // At least 1 to avoid division by zero
    // Add 20% padding and round up to next nice number
    const paddedMax = maxTotal * 1.2;
    // Round up to a nice number (multiple of 5 for small numbers, 10 for larger)
    const max = maxTotal <= 10 ? Math.ceil(paddedMax / 5) * 5 : Math.ceil(paddedMax / 10) * 10;

    let stepSize: number;
    if (max <= 10) {
      stepSize = Math.ceil(max / 4 / 1) * 1; // Steps of 1-3 for small ranges
    } else if (max <= 25) {
      stepSize = Math.ceil(max / 4 / 5) * 5; // Steps of 5
    } else {
      stepSize = Math.ceil(max / 4 / 10) * 10; // Steps of 10 or more
    }

    return { max, stepSize };
  }

  getChartColors(): ChartColors {
    const getCssVariable = (variableName: string): string => {
      return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    };

    return {
      lineBlue: getCssVariable('--widget-chart-line-blue') || '#1d5eff',
      lineBlueOpacity90:
        getCssVariable('--widget-chart-line-blue-opacity-90') || 'rgba(29, 94, 255, 0.9)',
      lineBlueOpacity60:
        getCssVariable('--widget-chart-line-blue-opacity-60') || 'rgba(29, 94, 255, 0.6)',
      lineBlueOpacity40:
        getCssVariable('--widget-chart-line-blue-opacity-40') || 'rgba(29, 94, 255, 0.4)',
      lineBlueOpacity30:
        getCssVariable('--widget-chart-line-blue-opacity-30') || 'rgba(29, 94, 255, 0.3)',
      lineBlueOpacity10:
        getCssVariable('--widget-chart-line-blue-opacity-10') || 'rgba(29, 94, 255, 0.1)',
      pointBackground: getCssVariable('--widget-chart-point-background') || '#ffffff',
      axisTextLight: getCssVariable('--widget-chart-axis-text-light') || 'rgba(0, 0, 0, 0.7)',
      axisTextDark: getCssVariable('--widget-chart-axis-text-dark') || 'rgba(255, 255, 255, 0.7)',
      axisGridLight: getCssVariable('--widget-chart-axis-grid-light') || 'rgba(0, 0, 0, 0.1)',
      axisGridDark: getCssVariable('--widget-chart-axis-grid-dark') || 'rgba(255, 255, 255, 0.1)',
    };
  }

  groupDataByPeriod(
    data: IListNonConformanceModel[],
    period: TimePeriod,
    customDateRange?: { from: Date; to: Date }
  ): DeviationDataPoint[] {
    const now = new Date();
    const dataPoints: DeviationDataPoint[] = [];
    const grouped = new Map<string, IListNonConformanceModel[]>();

    // Calculate the start date based on the period
    let normalizedStartDate: Date | null;
    let normalizedEndDate: Date;
    if (period === TimePeriod.Custom && customDateRange) {
      // For Custom period, use the provided date range
      normalizedStartDate = this.normalizeDate(customDateRange.from);
      normalizedEndDate = new Date(customDateRange.to);
      normalizedEndDate.setHours(23, 59, 59, 999); // End of day for inclusive comparison
    } else if (period === TimePeriod.AllTime) {
      // For AllTime, set startDate to a very early date to include all data
      const startDate = new Date();
      startDate.setFullYear(DeviationsOverTimeProcessingService.ALL_TIME_START_YEAR, 0, 1);
      normalizedStartDate = this.normalizeDate(startDate);
      normalizedEndDate = new Date(now);
      normalizedEndDate.setHours(23, 59, 59, 999); // End of today for inclusive comparison
    } else {
      normalizedStartDate = this.normalizeDate(this.getPeriodStartDate(period, now));
      normalizedEndDate = new Date(now);
      normalizedEndDate.setHours(23, 59, 59, 999); // End of today for inclusive comparison
    }
    if (!normalizedStartDate) {
      return []; // Invalid start date
    }

    // Filter and group data by the actual time period
    data.forEach((item) => {
      if (!item.nonConformanceDate) {
        return; // Skip items without dates
      }
      const date = this.normalizeDate(new Date(item.nonConformanceDate));
      if (!date) {
        return; // Skip invalid dates
      }

      // For AllTime, include all data regardless of date
      // For other periods, only include items within the time period
      if (period !== TimePeriod.AllTime) {
        if (date < normalizedStartDate || date > normalizedEndDate) {
          return;
        }
      }

      let key: string;
      // YearToDate: if still in January, group by day; otherwise group by month
      // Makes sense innit? I thought it makes sense, so I added it.
      const isYearToDateInJanuary = period === TimePeriod.YearToDate && now.getMonth() === 0;
      // For custom ranges, determine grouping based on range length
      const isCustomShortRange =
        period === TimePeriod.Custom &&
        customDateRange &&
        this.getDaysDifference(customDateRange.from, customDateRange.to) <= 30;

      if (
        period === TimePeriod.Last7Days ||
        period === TimePeriod.Last14Days ||
        period === TimePeriod.Last30Days ||
        isYearToDateInJanuary ||
        isCustomShortRange
      ) {
        // Group by full date for uniqueness (handles month boundaries)
        const dayOfMonth = date.getDate();
        key = `${date.getFullYear()}-${date.getMonth()}-${dayOfMonth}`;
      } else {
        // Group by year-month for proper filtering (includes AllTime, YearToDate (Feb+), Last6Months, Last12Months, Custom long range)
        const yearMonth = `${date.getFullYear()}-${date.getMonth()}`;
        key = yearMonth;
      }

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    // Generate data points for the actual period
    if (period === TimePeriod.AllTime) {
      this.generateAllTimeDataPoints(grouped, dataPoints);
    } else if (period === TimePeriod.Custom && customDateRange) {
      const days = this.getDaysDifference(customDateRange.from, customDateRange.to);
      if (days <= 30) {
        // For short custom ranges (<= 30 days), group by day
        this.generateCustomDayPeriodDataPoints(customDateRange, grouped, dataPoints);
      } else {
        // For longer custom ranges, group by month
        this.generateCustomMonthPeriodDataPoints(customDateRange, grouped, dataPoints);
      }
    } else if (
      period === TimePeriod.Last7Days ||
      period === TimePeriod.Last14Days ||
      period === TimePeriod.Last30Days
    ) {
      const days = period === TimePeriod.Last7Days ? 7 : period === TimePeriod.Last14Days ? 14 : 30;
      this.generateDayPeriodDataPoints(now, grouped, dataPoints, days);
    } else if (period === TimePeriod.YearToDate) {
      this.generateYearToDateDataPoints(now, grouped, dataPoints);
    } else if (period === TimePeriod.Last6Months || period === TimePeriod.Last12Months) {
      const months = period === TimePeriod.Last6Months ? 6 : 12;
      this.generateMonthPeriodDataPoints(now, grouped, dataPoints, months);
    }

    return dataPoints;
  }

  categorizeDeviations(items: IListNonConformanceModel[], period: string): DeviationDataPoint {
    const typeCounts = new Map<string, number>();

    items.forEach((item) => {
      const typeName = item.type?.name;
      if (typeName) {
        const currentCount = typeCounts.get(typeName) || 0;
        typeCounts.set(typeName, currentCount + 1);
      }
    });

    return {
      period,
      total: items.length,
      typeCounts,
      items,
    };
  }

  generateTypeColorMappings(uniqueTypes: string[]): TypeColorMapping[] {
    const colors = this.generateColorPalette(uniqueTypes.length);
    return uniqueTypes.map((typeName, index) => ({
      typeName,
      color: colors[index],
    }));
  }

  generateColorPalette(count: number): string[] {
    const colors: string[] = [];
    const maxCount = Math.min(count, 50);

    // tried to come up with a professional color palette, approved initially by Hakon
    const professionalBaseColors = [
      // Blue
      { h: 210, s: 60, l: 50 }, // Corporate blue
      { h: 200, s: 55, l: 48 }, // Steel blue
      { h: 220, s: 65, l: 45 }, // Navy blue
      { h: 195, s: 50, l: 52 }, // Sky blue
      { h: 205, s: 58, l: 47 }, // Slate blue

      // Teals/Cyans
      { h: 180, s: 45, l: 50 }, // Teal
      { h: 185, s: 50, l: 48 }, // Cyan-teal
      { h: 175, s: 40, l: 52 }, // Aqua
      { h: 190, s: 55, l: 45 }, // Deep teal

      // Greens
      { h: 150, s: 40, l: 48 }, // Muted green
      { h: 140, s: 45, l: 50 }, // Forest green
      { h: 160, s: 35, l: 52 }, // Sage green
      { h: 145, s: 50, l: 45 }, // Olive green

      // Oranges/Ambers
      { h: 30, s: 55, l: 50 }, // Amber
      { h: 35, s: 50, l: 48 }, // Burnt orange
      { h: 25, s: 60, l: 45 }, // Rust
      { h: 40, s: 45, l: 52 }, // Golden

      // Grays and Neutrals
      { h: 200, s: 15, l: 50 }, // Blue-gray
      { h: 180, s: 20, l: 48 }, // Teal-gray
      { h: 210, s: 18, l: 45 }, // Slate
      { h: 195, s: 12, l: 52 }, // Light gray-blue
    ];

    // Variations for each base color to create more distinct shades
    const variations = [
      { s: 0, l: 0 }, // Base
      { s: -5, l: -3 }, // Slightly less saturated, darker
      { s: 5, l: 3 }, // Slightly more saturated, lighter
      { s: -8, l: 5 }, // Less saturated, lighter
      { s: 8, l: -5 }, // More saturated, darker
    ];

    for (let i = 0; i < maxCount; i++) {
      const baseColor = professionalBaseColors[i % professionalBaseColors.length];
      const variation =
        variations[Math.floor(i / professionalBaseColors.length) % variations.length];
      const saturation = Math.max(10, Math.min(70, baseColor.s + variation.s));
      const lightness = Math.max(35, Math.min(60, baseColor.l + variation.l));

      const color = `hsl(${baseColor.h}, ${saturation}%, ${lightness}%)`;
      colors.push(color);
    }

    return colors;
  }

  calculateLegendData(
    chartData: DeviationDataPoint[],
    selectedTypeIds: string[],
    allDeviationOptions: IdName[],
    typeColorMappings: TypeColorMapping[]
  ): LegendData {
    // Check if all deviations are selected (empty array or all options selected)
    const areAllDeviationsSelected =
      selectedTypeIds.length === 0 || selectedTypeIds.length === allDeviationOptions.length;

    // Calculate total count for selected period
    const totalDeviationsCount = chartData.reduce((sum, dataPoint) => sum + dataPoint.total, 0);

    let selectedDeviationCounts: { typeName: string; count: number; color: string }[] = [];

    if (areAllDeviationsSelected) {
      selectedDeviationCounts = [];
    } else {
      selectedDeviationCounts = selectedTypeIds
        .map((typeId) => {
          const typeName = typeId;
          // Calculate total count for this type across all periods
          const count = chartData.reduce((sum, dataPoint) => {
            return sum + (dataPoint.typeCounts.get(typeName) || 0);
          }, 0);

          // Find the color for this type (use Map for O(1) lookup)
          const colorMap = new Map(typeColorMappings.map((m) => [m.typeName, m.color]));
          const defaultColor =
            getComputedStyle(document.documentElement)
              .getPropertyValue('--widget-legend-default-color')
              .trim() || '#4A90E2';
          const color = colorMap.get(typeName) || defaultColor;

          return { typeName, count, color };
        })
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);
    }

    return {
      totalDeviationsCount,
      selectedDeviationCounts,
      areAllDeviationsSelected,
    };
  }

  // Trend calculation method - temporarily commented out, may be needed in the future
  // calculateTrend(
  //   chartData: DeviationDataPoint[],
  //   period: TimePeriod,
  //   allOpenNonConformances: IListNonConformanceModel[],
  //   allCompletedNonConformances: IListNonConformanceModel[],
  //   allClosedNonConformances: IListNonConformanceModel[],
  //   selectedVesselIds: string[],
  //   selectedTypeIds: string[],
  //   allDeviationOptions: IdName[],
  //   customDateRange?: { from: Date; to: Date }
  // ): TrendCalculationResult {
  //   // For "AllTime", we cannot calculate a trend as there's no previous period
  //   if (period === TimePeriod.AllTime) {
  //     return {
  //       trend: TrendValueEnum.Insufficient,
  //       trendText: 'trendCannotBeCalculated',
  //       hasInsufficientData: true,
  //     };
  //   }

  //   // Get current period's data
  //   if (chartData.length === 0) {
  //     return {
  //       trend: TrendValueEnum.Insufficient,
  //       trendText: 'trendCannotBeCalculated',
  //       hasInsufficientData: true,
  //     };
  //   }

  //   // Handle custom date range
  //   let currentPeriodMonths: number;
  //   let currentTotal: number;
  //   let currentAvg: number;
  //   let previousPeriodStart: Date;
  //   let previousPeriodEnd: Date;

  //   if (period === TimePeriod.Custom && customDateRange) {
  //     // For custom date range, calculate based on days
  //     const daysInRange = this.getDaysDifference(customDateRange.from, customDateRange.to) + 1; // +1 to include both start and end days
  //     currentTotal = chartData.reduce((sum, d) => sum + d.total, 0);
  //     currentAvg = currentTotal / daysInRange;

  //     // Calculate previous period: same number of days before the custom range
  //     previousPeriodEnd = new Date(customDateRange.from);
  //     previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1); // Day before custom range starts
  //     previousPeriodEnd.setHours(23, 59, 59, 999);

  //     previousPeriodStart = new Date(previousPeriodEnd);
  //     previousPeriodStart.setDate(previousPeriodStart.getDate() - (daysInRange - 1)); // Same number of days as custom range
  //     previousPeriodStart.setHours(0, 0, 0, 0);

  //     currentPeriodMonths = daysInRange / 30; // Convert to months equivalent for consistency
  //   } else {
  //     // Calculate current period's average deviations per month
  //     currentPeriodMonths = this.getPeriodLengthInMonths(period);
  //     currentTotal = chartData.reduce((sum, d) => sum + d.total, 0);
  //     currentAvg = currentTotal / currentPeriodMonths;

  //     // Calculate previous period's date range
  //     const now = new Date();
  //     const currentPeriodStart = this.getPeriodStartDate(period, now);

  //     if (period === TimePeriod.YearToDate) {
  //       // Previous year to date: same period in previous year (Jan 1 to same day last year)
  //       const currentYear = now.getFullYear();
  //       const currentMonth = now.getMonth();
  //       const currentDay = now.getDate();

  //       previousPeriodStart = new Date(currentYear - 1, 0, 1); // January 1st of previous year
  //       previousPeriodStart.setHours(0, 0, 0, 0);

  //       previousPeriodEnd = new Date(currentYear - 1, currentMonth, currentDay);
  //       previousPeriodEnd.setHours(23, 59, 59, 999);
  //     } else {
  //       previousPeriodEnd = new Date(currentPeriodStart);
  //       previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1); // Day before current period starts
  //       previousPeriodEnd.setHours(23, 59, 59, 999);

  //       previousPeriodStart = new Date(previousPeriodEnd);
  //       if (period === TimePeriod.Last7Days) {
  //         previousPeriodStart.setDate(previousPeriodStart.getDate() - 6); // 7 days total (inclusive)
  //       } else if (period === TimePeriod.Last14Days) {
  //         previousPeriodStart.setDate(previousPeriodStart.getDate() - 13); // 14 days total (inclusive)
  //       } else if (period === TimePeriod.Last30Days) {
  //         previousPeriodStart.setDate(previousPeriodStart.getDate() - 29); // 30 days total (inclusive)
  //       } else if (period === TimePeriod.Last6Months) {
  //         previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 6);
  //       } else {
  //         // Last12Months
  //         previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 12);
  //       }
  //       previousPeriodStart.setHours(0, 0, 0, 0);
  //     }
  //   }

  //   // Get filtered data for previous period (apply same filters as current period)
  //   // Apply department and type filters
  //   const filteredData = this.dataService.applyFilters(
  //     allOpenNonConformances,
  //     allCompletedNonConformances,
  //     allClosedNonConformances,
  //     selectedVesselIds,
  //     selectedTypeIds,
  //     allDeviationOptions
  //   );
  //   let previousPeriodData = [
  //     ...filteredData.open,
  //     ...filteredData.completed,
  //     ...filteredData.closed,
  //   ];

  //   // Filter by date range for previous period
  //   previousPeriodData = previousPeriodData.filter((item) => {
  //     if (!item.nonConformanceDate) {
  //       return false;
  //     }
  //     const date = this.normalizeDate(new Date(item.nonConformanceDate));
  //     if (!date) {
  //       return false;
  //     }
  //     return date >= previousPeriodStart && date <= previousPeriodEnd;
  //   });

  //   // Check if we have enough data for previous period
  //   if (previousPeriodData.length === 0) {
  //     return {
  //       trend: TrendValueEnum.Insufficient,
  //       trendText: 'trendCannotBeCalculated',
  //       hasInsufficientData: true,
  //     };
  //   }

  //   // Calculate previous period's average deviations
  //   const previousTotal = previousPeriodData.length;
  //   // For custom date range, use days; for others, use months
  //   const previousPeriodLength =
  //     period === TimePeriod.Custom && customDateRange
  //       ? this.getDaysDifference(customDateRange.from, customDateRange.to) + 1
  //       : currentPeriodMonths;
  //   const previousAvg = previousTotal / previousPeriodLength;

  //   // Handle division by zero when previousAvg is 0
  //   if (previousAvg === 0 || isNaN(previousAvg)) {
  //     if (currentAvg > 0 && !isNaN(currentAvg)) {
  //       // Going from 0 to positive deviations is a negative trend (bad)
  //       return {
  //         trend: TrendValueEnum.VeryNegative,
  //         trendText: 'very negative',
  //         hasInsufficientData: false,
  //       };
  //     } else {
  //       // Both are 0 or invalid, neutral trend
  //       return {
  //         trend: TrendValueEnum.Neutral,
  //         trendText: 'neutral',
  //         hasInsufficientData: false,
  //       };
  //     }
  //   }

  //   // Calculate percentage change: (current - previous) / previous * 100
  //   // Negative change = decrease = good (positive trend)
  //   // Positive change = increase = bad (negative trend)
  //   const change = ((currentAvg - previousAvg) / previousAvg) * 100;

  //   // Determine trend based on thresholds
  //   return this.calculateTrendFromChange(change);
  // }

  getPeriodStartDate(period: TimePeriod, referenceDate: Date): Date {
    const startDate = new Date(referenceDate);
    if (period === TimePeriod.YearToDate) {
      // Year to Date: from January 1st of current year
      startDate.setMonth(0, 1); // January 1st
      startDate.setFullYear(referenceDate.getFullYear());
    } else if (period === TimePeriod.Last7Days) {
      startDate.setDate(referenceDate.getDate() - 7);
    } else if (period === TimePeriod.Last14Days) {
      startDate.setDate(referenceDate.getDate() - 14);
    } else if (period === TimePeriod.Last30Days) {
      startDate.setDate(referenceDate.getDate() - 30);
    } else if (period === TimePeriod.Last90Days) {
      startDate.setDate(referenceDate.getDate() - 90);
    } else if (period === TimePeriod.Last6Months) {
      startDate.setMonth(referenceDate.getMonth() - 6);
    } else {
      // Last12Months
      startDate.setMonth(referenceDate.getMonth() - 12);
    }
    startDate.setHours(0, 0, 0, 0);
    return startDate;
  }

  getPeriodLengthInMonths(period: TimePeriod): number {
    if (period === TimePeriod.YearToDate) {
      // Year to Date: from January 1st to today
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // Months are 0-indexed, so add 1
      // Calculate fraction of current month that has passed
      const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const daysPassedInCurrentMonth = now.getDate();
      const fractionOfCurrentMonth = daysPassedInCurrentMonth / daysInCurrentMonth;
      return currentMonth - 1 + fractionOfCurrentMonth;
    } else if (period === TimePeriod.Last7Days) {
      return 7 / 30; // Approximately 0.23 months
    } else if (period === TimePeriod.Last14Days) {
      return 14 / 30; // Approximately 0.47 months
    } else if (period === TimePeriod.Last30Days) {
      return 30 / 30; // 1 month equivalent
    } else if (period === TimePeriod.Last6Months) {
      return 6;
    } else {
      // Last12Months
      return 12;
    }
  }

  getMonthName(date: Date, format: 'long' | 'short' = 'long'): string {
    // For full month names, use translation keys
    if (format === 'long') {
      const monthIndex = date.getMonth();
      const monthKeys = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
      ];
      const monthKey = monthKeys[monthIndex];
      return this.translateService.instant(monthKey);
    }
    // For short month names, use existing locale-based approach
    const currentLang = this.languageService.getCurrentLanguage();
    const locale = this.languageService.getLocaleForLanguage(currentLang);
    return this.getMonthNameWithFallback(date, format, locale);
  }

  getMonthIndex(monthShort: string): number {
    const monthLower = monthShort.toLowerCase();
    // Handle common variations of month abbreviations
    const monthMap: { [key: string]: number } = {
      jan: 0,
      january: 0,
      feb: 1,
      february: 1,
      mar: 2,
      march: 2,
      apr: 3,
      april: 3,
      may: 4,
      jun: 5,
      june: 5,
      jul: 6,
      july: 6,
      aug: 7,
      august: 7,
      sep: 8,
      sept: 8, // Some locales use "Sept" instead of "Sep", at least that's what the internet says
      september: 8,
      oct: 9,
      october: 9,
      nov: 10,
      november: 10,
      dec: 11,
      december: 11,
    };
    return monthMap[monthLower] ?? -1;
  }

  formatPeriodWithFullMonth(
    period: string,
    items?: DashboardListItem[],
    dateField?: string
  ): string {
    // First, try to translate the period label (handles month abbreviations)
    const translatedLabels = this.translateMonthLabels([period]);
    const translatedPeriod = translatedLabels[0];

    // If translation changed the period (meaning it was a month), check if it's "Month Year" format
    const monthYearMatch = translatedPeriod.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (monthYearMatch) {
      // It's already in "Month Year" format, convert to full month name
      const monthShort = monthYearMatch[1];
      const year = monthYearMatch[2];
      const monthIndex = this.getMonthIndex(monthShort);
      if (monthIndex !== -1) {
        const date = new Date(parseInt(year), monthIndex, 1);
        const monthFull = this.getMonthName(date, 'long');
        return `${monthFull} ${year}`;
      }
    }

    // Try to parse just month name (e.g., "Nov" or "Oct")
    // Check both the original period and translated period to handle cases where
    // translation doesn't change the period (e.g., "Oct" in English locale)
    const monthMatch = translatedPeriod.match(/^[A-Za-z]+$/);
    if (monthMatch) {
      const monthShort = monthMatch[0];
      const monthIndex = this.getMonthIndex(monthShort);
      if (monthIndex !== -1) {
        // It's a month abbreviation, convert to full month name
        const now = new Date();
        const date = new Date(now.getFullYear(), monthIndex, 1);
        const monthFull = this.getMonthName(date, 'long');
        return `${monthFull} ${now.getFullYear()}`;
      }
    }

    // For day numbers (Last30Days), try to get date from first item
    const dayMatch = period.match(/^\d+$/);
    if (dayMatch && items && items.length > 0) {
      const firstItem = items[0];
      const fieldName = dateField || 'nonConformanceDate';
      const itemDate = firstItem[fieldName];
      if (itemDate) {
        const date = this.normalizeDate(new Date(itemDate));
        if (date) {
          // Format as simple date YYYY-MM-DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
    }

    // Fallback: return translated period or original
    return translatedPeriod !== period ? translatedPeriod : period;
  }

  private normalizeDate(date: Date): Date | null {
    if (!date || isNaN(date.getTime())) {
      return null;
    }
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private getCurrentLocale(): string {
    // Use cached language if available, otherwise fetch from service and cache it
    const currentLang = this.currentLanguage || this.languageService.getCurrentLanguage();
    // Update cache if it wasn't set
    if (!this.currentLanguage) {
      this.currentLanguage = currentLang;
    }
    return this.languageService.getLocaleForLanguage(currentLang);
  }

  updateLanguage(language: string): void {
    this.currentLanguage = language;
  }

  private getMonthNameWithFallback(
    date: Date,
    format: 'short' | 'long' = 'short',
    locale: string
  ): string {
    // If Icelandic and Chrome doesn't support it, use manual translation
    if (locale === 'is-IS') {
      const monthIndex = date.getMonth();
      const monthNames =
        format === 'short'
          ? DeviationsOverTimeProcessingService.ICELANDIC_MONTHS_SHORT
          : DeviationsOverTimeProcessingService.ICELANDIC_MONTHS_LONG;
      const monthName = monthNames[monthIndex];
      // Capitalize first letter for consistency
      return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    }

    // For other languages, use browser's native toLocaleString
    const monthName = date.toLocaleString(locale, { month: format });
    // Capitalize first letter for consistency (ensures all months start with capital)
    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
  }

  private generateAllTimeDataPoints(
    grouped: Map<string, IListNonConformanceModel[]>,
    dataPoints: DeviationDataPoint[]
  ): void {
    const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
      const [yearA, monthA] = a.split('-').map(Number);
      const [yearB, monthB] = b.split('-').map(Number);
      if (yearA !== yearB) {
        return yearA - yearB;
      }
      return monthA - monthB;
    });

    const locale = this.getCurrentLocale();
    sortedKeys.forEach((key) => {
      const [year, month] = key.split('-').map(Number);
      const date = new Date(year, month, 1);
      const monthName = this.getMonthNameWithFallback(date, 'short', locale);
      // Include year in label for AllTime to distinguish between years
      const label = `${monthName} ${year}`;
      const items = grouped.get(key) || [];
      dataPoints.push(this.categorizeDeviations(items, label));
    });
  }

  private generateDayPeriodDataPoints(
    now: Date,
    grouped: Map<string, IListNonConformanceModel[]>,
    dataPoints: DeviationDataPoint[],
    days: number
  ): void {
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(now.getDate() - i);
      const normalizedDate = this.normalizeDate(date);
      if (!normalizedDate) {
        continue;
      }
      const dayOfMonth = normalizedDate.getDate();
      const month = normalizedDate.getMonth() + 1;
      const key = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}-${dayOfMonth}`;
      const items = grouped.get(key) || [];
      const label = `${dayOfMonth}.${month.toString().padStart(2, '0')}`;
      dataPoints.push(this.categorizeDeviations(items, label));
    }
  }

  private generateYearToDateDataPoints(
    now: Date,
    grouped: Map<string, IListNonConformanceModel[]>,
    dataPoints: DeviationDataPoint[]
  ): void {
    const locale = this.getCurrentLocale();
    // If still in January, show days; otherwise show months
    if (now.getMonth() === 0) {
      // Still in January: generate labels for days from January 1st to today
      const daysInJanuary = now.getDate();
      for (let day = 1; day <= daysInJanuary; day++) {
        const date = new Date(now.getFullYear(), 0, day);
        const normalizedDate = this.normalizeDate(date);
        if (!normalizedDate) {
          continue;
        }
        const month = normalizedDate.getMonth() + 1; // getMonth() is 0-based, so add 1
        const key = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}-${day}`;
        const items = grouped.get(key) || [];
        // Format as "day.month" (e.g., "22.01", "3.01", "11.01")
        const label = `${day}.${month.toString().padStart(2, '0')}`;
        dataPoints.push(this.categorizeDeviations(items, label));
      }
    } else {
      // February or later: generate labels for months from January to current month
      const currentMonth = now.getMonth();
      for (let i = 0; i <= currentMonth; i++) {
        const date = new Date(now.getFullYear(), i, 1);
        const normalizedDate = this.normalizeDate(date);
        if (!normalizedDate) {
          continue;
        }
        const monthName = this.getMonthNameWithFallback(normalizedDate, 'short', locale);
        const key = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}`;
        const items = grouped.get(key) || [];
        dataPoints.push(this.categorizeDeviations(items, monthName));
      }
    }
  }

  private generateMonthPeriodDataPoints(
    now: Date,
    grouped: Map<string, IListNonConformanceModel[]>,
    dataPoints: DeviationDataPoint[],
    months: number
  ): void {
    const locale = this.getCurrentLocale();
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(now.getMonth() - i);
      const normalizedDate = this.normalizeDate(date);
      if (!normalizedDate) {
        continue;
      }
      const monthName = this.getMonthNameWithFallback(normalizedDate, 'short', locale);
      const key = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}`;
      const items = grouped.get(key) || [];
      dataPoints.push(this.categorizeDeviations(items, monthName));
    }
  }

  private getDaysDifference(from: Date, to: Date): number {
    const fromDate = this.normalizeDate(from);
    const toDate = this.normalizeDate(to);
    if (!fromDate || !toDate) {
      return 0;
    }
    const diffTime = toDate.getTime() - fromDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  private generateCustomDayPeriodDataPoints(
    dateRange: { from: Date; to: Date },
    grouped: Map<string, IListNonConformanceModel[]>,
    dataPoints: DeviationDataPoint[]
  ): void {
    const fromDate = this.normalizeDate(dateRange.from);
    const toDate = new Date(dateRange.to);
    toDate.setHours(23, 59, 59, 999);

    if (!fromDate) {
      return;
    }

    const currentDate = new Date(fromDate);
    while (currentDate <= toDate) {
      const normalizedDate = this.normalizeDate(currentDate);
      if (!normalizedDate) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      const dayOfMonth = normalizedDate.getDate();
      const month = normalizedDate.getMonth() + 1;
      const key = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}-${dayOfMonth}`;
      const items = grouped.get(key) || [];
      const label = `${dayOfMonth}.${month.toString().padStart(2, '0')}`;
      dataPoints.push(this.categorizeDeviations(items, label));
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  private generateCustomMonthPeriodDataPoints(
    dateRange: { from: Date; to: Date },
    grouped: Map<string, IListNonConformanceModel[]>,
    dataPoints: DeviationDataPoint[]
  ): void {
    const locale = this.getCurrentLocale();
    const fromDate = this.normalizeDate(dateRange.from);
    const toDate = new Date(dateRange.to);
    toDate.setHours(23, 59, 59, 999);

    if (!fromDate) {
      return;
    }

    const currentDate = new Date(fromDate);
    currentDate.setDate(1); // Start from first day of month

    while (currentDate <= toDate) {
      const normalizedDate = this.normalizeDate(currentDate);
      if (!normalizedDate) {
        currentDate.setMonth(currentDate.getMonth() + 1);
        continue;
      }
      const monthName = this.getMonthNameWithFallback(normalizedDate, 'short', locale);
      const key = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}`;
      const items = grouped.get(key) || [];
      dataPoints.push(this.categorizeDeviations(items, monthName));
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }

  // Trend calculation helper method - temporarily commented out, may be needed in the future
  // private calculateTrendFromChange(change: number): TrendCalculationResult {
  //   if (change < DeviationsOverTimeProcessingService.TREND_VERY_POSITIVE_THRESHOLD) {
  //     // Decrease > 20% = very positive (good)
  //     return {
  //       trend: TrendValueEnum.VeryPositive,
  //       trendText: 'very positive',
  //       hasInsufficientData: false,
  //     };
  //   } else if (change < DeviationsOverTimeProcessingService.TREND_POSITIVE_THRESHOLD) {
  //     // Decrease 5-20% = positive (good)
  //     return {
  //       trend: TrendValueEnum.Positive,
  //       trendText: 'positive',
  //       hasInsufficientData: false,
  //     };
  //   } else if (change > DeviationsOverTimeProcessingService.TREND_VERY_NEGATIVE_THRESHOLD) {
  //     // Increase > 20% = very negative (bad)
  //     return {
  //       trend: TrendValueEnum.VeryNegative,
  //       trendText: 'very negative',
  //       hasInsufficientData: false,
  //     };
  //   } else if (change > DeviationsOverTimeProcessingService.TREND_NEGATIVE_THRESHOLD) {
  //     // Increase 5-20% = negative (bad)
  //     return {
  //       trend: TrendValueEnum.Negative,
  //       trendText: 'negative',
  //       hasInsufficientData: false,
  //     };
  //   } else {
  //     // Change between -5% and +5% = neutral
  //     return {
  //       trend: TrendValueEnum.Neutral,
  //       trendText: 'neutral',
  //       hasInsufficientData: false,
  //     };
  //   }
  // }
}
